import React, { useCallback } from 'react';
import Style from './settingsInput.module.scss';
import clsx from 'clsx';
import { ReactComponent as TooltipIcon } from 'assets/img/generalBuilder/tip_24.svg';
import { AppButton, AppButtonProps } from 'uikit/buttons';
import { Tooltip } from 'uikit/tooltip/tooltip.component';
import { AppTextInputProps } from 'uikit/inputs';
import { AppInputError } from 'uikit/wrappers';

type SettingsInputProps = AppTextInputProps & {
  badge?: React.JSX.Element;
  regex?: string;
  units?: string;
  inputClassName?: string;
  error?: string;
  hideErrorText?: boolean;
  tooltipText?: string;
  button?: Omit<AppButtonProps, 'variant'>;
};

export const SettingsInput = React.forwardRef<HTMLInputElement, SettingsInputProps>(function SettingsInput(
  {
    badge,
    className,
    units,
    type,
    multiple,
    disabled,
    error,
    tooltipText,
    inputClassName,
    hideErrorText,
    button,
    ...rest
  },
  ref,
) {
  const onKeyDownHandler = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.code === 'Enter' || event.code === 'NumpadEnter') {
      // @ts-ignore
      event.target.blur();
    }
  }, []);

  const {
    colorSchema,
    className: buttonClassName,
    ...buttonRest
  } = button ?? {
    colorSchema: 'basic',
  };

  return (
    <AppInputError hideBorder className={className} errors={error} hideText={hideErrorText}>
      <div className={Style.container}>
        {badge && <div className={Style.badge}>{badge}</div>}
        {type === 'color' && <input type={'color'} className={Style.colorIndicator} disabled={disabled} {...rest} />}
        {multiple ? (
          <textarea
            // @ts-ignore
            ref={ref}
            data-error={!!error}
            className={Style.textArea}
            disabled={disabled}
            {...rest}
          />
        ) : (
          <div className={Style.inputWithButton}>
            <input
              ref={ref}
              data-error={!!error}
              type={type === 'color' ? undefined : type}
              className={clsx(Style.input, { [Style.paddingRight]: !!button }, inputClassName)}
              disabled={disabled || type === 'color'}
              onKeyDown={onKeyDownHandler}
              {...rest}
            />
            {button && (
              // @ts-ignore
              <AppButton
                className={clsx(Style.button, buttonClassName)}
                variant={'icon-link'}
                colorSchema={colorSchema}
                {...buttonRest}
              />
            )}
          </div>
        )}

        {units && <span>{units}</span>}
        {tooltipText && (
          <Tooltip tooltipContent={tooltipText} calloutContainerStyles={Style.tooltipIcon}>
            <TooltipIcon />
          </Tooltip>
        )}
      </div>
    </AppInputError>
  );
});
